import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { Bell, BellNotification, MagicWand, WarningTriangle, Sparks, Reports, NavArrowRight } from "iconoir-react";

const ICONS = {
  welcome: Sparks,
  audit_complete: Reports,
  security_alert: WarningTriangle,
  weekly_digest: BellNotification,
  fix_generated: MagicWand,
};
const ACCENTS = {
  welcome: "text-cobalt",
  audit_complete: "text-emerald-700",
  security_alert: "text-intl",
  weekly_digest: "text-foreground",
  fix_generated: "text-foreground",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data?.count || 0);
    } catch (err) { console.error("loadCount failed:", err); }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/notifications?limit=20");
      setItems(r.data || []);
    } catch (err) { console.error("loadItems failed:", err); } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCount();
    pollRef.current = setInterval(loadCount, 30_000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  const onItemClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (err) { console.error("mark read failed:", err); }
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/mark-all-read"); } catch (err) { console.error("mark all read failed:", err); }
    setUnread(0);
    setItems((arr) => arr.map((n) => ({ ...n, read: true })));
  };

  const BellIcon = unread > 0 ? BellNotification : Bell;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notifications-bell"
          className="relative inline-flex items-center justify-center w-10 h-10 border border-border bg-card hover:bg-background transition-colors"
          aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        >
          <BellIcon width={18} height={18} strokeWidth={1.6} />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-intl text-white font-mono text-[10px] font-bold leading-[18px] text-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0 border border-border bg-card rounded-none shadow-none"
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">// inbox</div>
            <div className="font-app text-base font-medium">Notifications</div>
          </div>
          {unread > 0 && (
            <button
              onClick={markAll}
              data-testid="notifications-mark-all"
              className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-intl"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[480px] overflow-y-auto" data-testid="notifications-list">
          {loading && items.length === 0 ? (
            <div className="p-6 font-mono text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center" data-testid="notifications-empty">
              <Bell width={28} height={28} strokeWidth={1.4} className="mx-auto text-muted-foreground" />
              <div className="mt-3 font-marketing text-lg">You're all caught up.</div>
              <div className="text-sm text-muted-foreground mt-1">New activity will show up here.</div>
            </div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            const accent = ACCENTS[n.type] || "text-foreground";
            return (
              <button
                key={n.id}
                onClick={() => onItemClick(n)}
                data-testid={`notification-${n.id}`}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-background transition-colors ${n.read ? "opacity-60" : ""}`}
              >
                <div className={`mt-0.5 ${accent}`}>
                  <Icon width={18} height={18} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-app text-sm font-medium truncate">{n.title}</div>
                    <div className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">{timeAgo(n.created_at)}</div>
                  </div>
                  {n.body && <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{n.body}</div>}
                </div>
                {n.link && (
                  <div className="self-center text-muted-foreground">
                    <NavArrowRight width={14} height={14} />
                  </div>
                )}
                {!n.read && <span className="absolute right-2 top-3 w-1.5 h-1.5 bg-intl" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
